/**
 * The machines that read your organization's projects (§5.25) — each one a
 * device token, issued when an app was linked by an account holding a seat.
 *
 * They are listed apart from the licence's machines because they are a
 * different credential with a different reach: a licence key downloads and
 * updates the app, a device token reads and publishes projects and hands a
 * machine its secrets. Revoking one takes effect at that machine's next call;
 * its workspaces stay on its disk, and it simply stops being a copy of anything.
 */
import type React from 'react';
import { color, font } from '../../theme/tokens';
import { Callout, Row, RowAction, StatusCell, Table } from '../../components/primitives';
import { useConfirm } from '../../components/Modal';
import { api, apiPath } from '../../lib/api';
import type { DeviceTokenRow } from '../../lib/types';
import { failureText } from './shared';
import { DateCell } from '../../components/DateCell';

export function DeviceTokens({
  tokens,
  onChanged,
  onError
}: {
  tokens: DeviceTokenRow[];
  onChanged: () => void;
  onError: (message: string) => void;
}): React.JSX.Element | null {
  // Above the early return, because it holds state: a hook after a conditional
  // `return` is a hook this component sometimes does not call.
  const [dialog, ask] = useConfirm();
  if (tokens.length === 0) return null;
  const columns = '1.4fr 130px 130px 110px 90px';

  const revoke = async (token: DeviceTokenRow): Promise<void> => {
    const name = token.label ?? 'this computer';
    const agreed = await ask({
      title: `Revoke ${name}?`,
      lede: 'It stops reading your organization’s projects at its next call. Its workspaces stay on its disk.',
      confirm: 'Revoke it',
      danger: true
    });
    if (!agreed) return;
    try {
      await api.del(apiPath`/devices/tokens/${token.id}`);
      onChanged();
    } catch (failure) {
      onError(failureText(failure));
    }
  };

  return (
    <div style={{ marginTop: 28 }}>
      {dialog}
      <Table
        columns={columns}
        header={['Machine', 'Linked', 'Last used', 'Access', '']}
        toolbar={<div style={{ fontSize: 15, fontWeight: 600 }}>Reading your organization’s projects</div>}
      >
        {tokens.map((token) => (
          <Row key={token.id} columns={columns}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 500 }}>{token.label ?? 'Unnamed machine'}</div>
              <div style={{ fontFamily: font.mono, fontSize: 11.5, color: color.inkFaint, marginTop: 3 }}>
                {token.machine.slice(0, 12)}
              </div>
            </div>
            <DateCell at={token.createdAt} style={{ fontFamily: font.mono, fontSize: 12.5, color: color.inkSubtle }} />
            <DateCell at={token.lastUsedAt} style={{ fontFamily: font.mono, fontSize: 12.5 }} />
            {token.revokedAt ? <StatusCell tone="off" word="Revoked" /> : <StatusCell tone="ok" word="Active" />}
            {token.revokedAt ? (
              <span />
            ) : (
              <RowAction danger onClick={() => void revoke(token)}>
                Revoke
              </RowAction>
            )}
          </Row>
        ))}
      </Table>
      <div style={{ maxWidth: 700 }}>
        <Callout title="What these are">
          Linking Airlock while you hold a seat gives that machine a token of its own: it reads your organization’s
          projects, publishes the ones you change, and receives their secrets sealed for its keychain. It works only
          from the machine it was issued to, and one unused for ninety days stops working on its own.
        </Callout>
      </div>
    </div>
  );
}
